/**
 * gm-modal.js
 * Reusable dialog helper for Gilbert Maxwell sections.
 *
 * Markup contract:
 *   <gm-modal id="unique-id" class="gm-modal" hidden>
 *     <div class="gm-modal__backdrop" data-gm-modal-close></div>
 *     <div class="gm-modal__panel" data-gm-modal-panel role="dialog" aria-modal="true" aria-labelledby="...">
 *       <button type="button" data-gm-modal-close aria-label="Close">...</button>
 *       ...content...
 *     </div>
 *   </gm-modal>
 *
 * Open programmatically with element.open(triggerElement), or declaratively by
 * giving any button a data-gm-modal-target="unique-id" attribute.
 *
 * Provides: focus trap, Escape to close, backdrop click to close, body scroll
 * lock, and focus return to the trigger on close. Honors reduced motion.
 */

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const CLOSE_ANIMATION_MS = 220;

class GmModal extends HTMLElement {
  /** @type {HTMLElement | null} */
  #trigger = null;
  #isOpen = false;
  #bound = false;
  #previousBodyOverflow = '';
  #hideTimer = 0;

  connectedCallback() {
    if (this.#bound) return;
    this.#bound = true;
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    // If the editor removes the section while a dialog is open, release the
    // scroll lock and the document listener so the page is never stuck.
    if (this.#isOpen) {
      this.#isOpen = false;
      document.body.style.overflow = this.#previousBodyOverflow;
      document.removeEventListener('keydown', this.#onKeydown, true);
    }
    clearTimeout(this.#hideTimer);
  }

  get isOpen() {
    return this.#isOpen;
  }

  /**
   * Opens the dialog.
   * @param {Element | null} [trigger] - The element that opened the dialog. Focus returns to it on close.
   */
  open(trigger) {
    if (this.#isOpen) return;
    this.#isOpen = true;
    this.#trigger = trigger instanceof HTMLElement ? trigger : null;

    clearTimeout(this.#hideTimer);
    this.removeAttribute('hidden');
    // Force a style flush so the is-open transition can run from the hidden state.
    void this.offsetWidth;
    this.classList.add('is-open');

    this.#previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.#onKeydown, true);

    const panel = this.querySelector('[data-gm-modal-panel]');
    const target = this.#focusables()[0] || panel;
    if (target instanceof HTMLElement) {
      if (target === panel) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }
  }

  close() {
    if (!this.#isOpen) return;
    this.#isOpen = false;
    this.classList.remove('is-open');

    document.body.style.overflow = this.#previousBodyOverflow;
    document.removeEventListener('keydown', this.#onKeydown, true);

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    clearTimeout(this.#hideTimer);
    this.#hideTimer = setTimeout(() => this.setAttribute('hidden', ''), reduceMotion ? 0 : CLOSE_ANIMATION_MS);

    if (this.#trigger && this.#trigger.isConnected) {
      this.#trigger.focus({ preventScroll: true });
    }
    this.#trigger = null;
  }

  /** @returns {HTMLElement[]} visible focusable elements inside the dialog */
  #focusables() {
    return Array.from(this.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (element) => element instanceof HTMLElement && element.getClientRects().length > 0
    );
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target && target.closest('[data-gm-modal-close]')) {
      event.preventDefault();
      this.close();
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (!this.#isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusables = this.#focusables();
    if (focusables.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    const inside = active instanceof Node && this.contains(active);

    if (event.shiftKey) {
      if (!inside || active === first) {
        event.preventDefault();
        last.focus();
      }
    } else if (!inside || active === last) {
      event.preventDefault();
      first.focus();
    }
  };
}

if (!customElements.get('gm-modal')) {
  customElements.define('gm-modal', GmModal);
}

// Declarative openers: any element with data-gm-modal-target opens the matching
// dialog. Module scope runs once per page, so this listener is registered once.
document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-gm-modal-target]') : null;
  if (!trigger) return;
  const modal = document.getElementById(trigger.getAttribute('data-gm-modal-target') || '');
  if (modal instanceof GmModal) {
    event.preventDefault();
    modal.open(trigger);
  }
});

export { GmModal };
